import { supabase, extractEdgeFunctionError } from '../data/client';
import ErrorHandler from '../utils/ErrorHandler';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { isPaymentMethodUsable } from '@/utils/paymentMethodStatus';

// Rainforest prod bundles drop the env prefix entirely:
//   sandbox → static.rainforestpay.com/sandbox.<bundle>.js
//   prod    → static.rainforestpay.com/<bundle>.js
// Sandbox session keys start with sbx_; prod keys have no prefix — the bundle
// always follows the key's environment so the two can never mismatch.
function getBundleUrl(sessionKey, bundle) {
    const prefix = sessionKey?.startsWith('sbx_') ? 'sandbox.' : '';
    return `https://static.rainforestpay.com/${prefix}${bundle}.js`;
}

export const getGivingMerchant = async (organizationID) => {
  // GivingMerchants SELECT is admin/owner-only (RLS, plan §5.6). The
  // SECURITY DEFINER get_giving_status() RPC is the member-safe replacement
  // and already matches this contract: JWT org scoping, billingProfile with
  // isSpecial instead of the raw name, no rawPayload/merchant ids.
  const { data, error } = await supabase.rpc('get_giving_status');
  if (error) ErrorHandler(Error(error.message), { query: 'getGivingMerchant', organizationID, error }, 'Failed to load giving status');
  return data;
};

export const getPublicGivingStatus = async (orgHandle, paymentMethodIDs = []) => {
  const { data, error } = await supabase.functions.invoke('get-public-giving-status', { body: { orgHandle, ...(paymentMethodIDs.length ? { paymentMethodIDs } : {}) } });
  if (error) throw error;
  return data || { enableGiving: false, merchantStatus: null };
};

export const enableGiving = async (organizationID) => {
  const { data, error } = await supabase.functions.invoke('rainforest-enable-giving', { body: { organizationID } });
  if (error) throw error;
  return data;
};

export const disableGiving = async (organizationID) => {
  const { data, error } = await supabase.functions.invoke('rainforest-disable-giving', { body: { organizationID } });
  if (error) throw error;
  return data;
};

export const updateGivingFeeChoice = async ({ organizationID, allowed }) => {
  const { error } = await supabase.from('Organizations').update({ allowGiversToChooseCoverFees: allowed }).eq('id', organizationID);
  if (error) throw new Error(error.message);
};

export const updateGivingAppearance = async ({ organizationID, appearance }) => {
  const { data, error } = await supabase
    .from('GivingMerchants')
    .update({ appearance })
    .eq('organizationID', organizationID)
    .select('appearance')
    .single();
  if (error) throw error;
  return data.appearance;
};

export const getOnboardingSession = async (organizationID) => {
  const { data, error } = await supabase.functions.invoke('rainforest-onboarding-session', { body: { organizationID } });
  if (error) throw error;
  return data;
};

/**
 * SANDBOX ONLY — simulate a merchant application decision (Rainforest test
 * endpoint). Only call against a non-prod Supabase project; the edge function
 * rejects prod environments.
 */
export const simulateApplicationStatus = async ({ organizationID, status, underwritingReviewTypes }) => {
  const { data, error } = await supabase.functions.invoke('rainforest-simulate-application-status', {
    body: {
      organizationID,
      status,
      ...(underwritingReviewTypes ? { underwriting_review_types: underwritingReviewTypes } : {}),
    },
  });
  if (error) {
    // FunctionsHttpError wraps the Response in .context — extract the actual error body.
    let message = error.message;
    try {
      const body = error.context?.body ? await error.context.json() : null;
      if (body?.error) message = body.error;
    } catch { /* response body may not be JSON */ }
    throw new Error(message);
  }
  return data;
};

export const createPayinConfig = async (payload) => {
  const { data, error } = await supabase.functions.invoke('rainforest-create-payin-config', { body: payload });
  if (error) {
    // FunctionsHttpError wraps the Response in .context — extract the actual error body.
    let message = error.message;
    try {
      const body = error.context?.body ? await error.context.json() : null;
      if (body?.error) message = body.error;
    } catch { /* response body may not be JSON */ }
    throw new Error(message);
  }
  return data;
};

/**
 * Unauthenticated version of createPayinConfig — calls the edge function
 * without an Authorization header so it enters the public (orgHandle) path.
 * Used when a logged-in user gives to an org they don't belong to, or when
 * no session exists. The apikey header is still required by the Supabase gateway.
 */
export const createPayinConfigUnauthenticated = async (payload) => {
  const res = await fetch(
    `${supabase.supabaseUrl}/functions/v1/rainforest-create-payin-config`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: supabase.supabaseKey },
      body: JSON.stringify(payload),
    },
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to start payment');
  return json;
};

const invokePaymentMethodConfig = async (payload) => {
  const { data, error } = await supabase.functions.invoke('rainforest-create-payment-method-config', { body: payload });
  if (error) {
    let message = error.message;
    try {
      const body = error.context?.body ? await error.context.json() : null;
      if (body?.error) message = body.error;
    } catch { /* response body may not be JSON */ }
    throw new Error(message);
  }
  return data;
};

const invokePaymentMethodConfigUnauthenticated = async (payload) => {
  const res = await fetch(
    `${supabase.supabaseUrl}/functions/v1/rainforest-create-payment-method-config`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: supabase.supabaseKey },
      body: JSON.stringify(payload),
    },
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to save payment method');
  return json;
};

export const createPaymentMethodConfig = async (payload) =>
  invokePaymentMethodConfig({ ...payload, action: 'create' });

export const createPaymentMethodConfigUnauthenticated = async (payload) =>
  invokePaymentMethodConfigUnauthenticated({ ...payload, action: 'create' });

export const finalizePaymentMethodConfig = async (payload) =>
  invokePaymentMethodConfig({ ...payload, action: 'finalize' });

export const finalizePaymentMethodConfigUnauthenticated = async (payload) =>
  invokePaymentMethodConfigUnauthenticated({ ...payload, action: 'finalize' });

/**
 * Poll for a payin record with the given payinConfigID via the
 * rainforest-poll-payin edge function (service role). Returns the payin
 * record or null if not found within the timeout.
 *
 * Always goes through the edge function — querying GivingPayins directly
 * requires the 'View Contributions' permission under RLS, so ordinary donors
 * would poll forever and time out. The edge function returns
 * { found, payin: { payinID, status, methodType, amount,
 * card, ach, applePay, createdAt } } — note: no rawPayload, no paymentMethodID.
 * `applePay` is present when methodType is 'APPLE_PAY':
 * { type, brand, brand_desc, description }.
 *
 * @param {string} payinConfigID
 * @param {object} [options]
 * @param {number} [options.intervalMs]
 * @param {number} [options.timeoutMs]
 * @param {boolean} [options.authenticated] Deprecated — accepted but ignored.
 */
export const pollPayinStatus = async (payinConfigID, { intervalMs = 2000, timeoutMs = 120000 } = {}) => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const res = await fetch(
      `${supabase.supabaseUrl}/functions/v1/rainforest-poll-payin?payinConfigID=${encodeURIComponent(payinConfigID)}`,
      { headers: { apikey: supabase.supabaseKey, Authorization: `Bearer ${supabase.supabaseKey}` } },
    );
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || json.message || 'Poll failed');
    if (json.found) return json.payin;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
};

/**
 * Remove (deactivate) a saved payment method via edge function.
 */
export const removePaymentMethod = async (paymentMethodID) => {
  const { data, error } = await supabase.functions.invoke('rainforest-remove-payment-method', { body: { paymentMethodID } });
  if (error) throw error;
  return data;
};

export const getPaymentMethods = async ({ organizationID, personID }) => {
  const { data, error } = await supabase
    .from('GivingPaymentMethods')
    .select('*')
    .eq('organizationID', organizationID)
    .eq('personID', personID)
    .is('removedAt', null)
    .or('isActive.eq.true,achReturnedAt.not.is.null')
    .order('isDefault', { ascending: false })
    .order('createdAt', { ascending: false });
  if (error) ErrorHandler(Error(error.message), { query: 'getPaymentMethods', organizationID, personID, error }, 'Failed to load saved payment methods');
  return data || [];
};

const localPaymentMethodsKey = (orgHandle) => `@reach_giving_payment_methods_${orgHandle}`;

const readLocalPaymentMethods = async (orgHandle) => {
  const raw = await AsyncStorage.getItem(localPaymentMethodsKey(orgHandle));
  const methods = raw ? JSON.parse(raw) : [];
  return Array.isArray(methods) ? methods : [];
};

export const getLocalPaymentMethods = async (orgHandle) => {
  try {
    const methods = await readLocalPaymentMethods(orgHandle);
    if (!methods.length) return [];
    const ids = [...new Set(methods.map(method => method.paymentMethodID || method.id).filter(Boolean))];
    const states = [];
    for (let offset = 0; offset < ids.length; offset += 20) {
      const status = await getPublicGivingStatus(orgHandle, ids.slice(offset, offset + 20));
      states.push(...(status.paymentMethodStates || []));
    }
    return methods.map(method => ({ ...method, ...states.find(state => state.paymentMethodID === (method.paymentMethodID || method.id)) }));
  } catch (e) {
    console.error('Error loading local payment methods:', e);
    return [];
  }
};

export const saveLocalPaymentMethod = async (orgHandle, method) => {
  try {
    const existing = await readLocalPaymentMethods(orgHandle);
    const filtered = existing.filter((m) => m.paymentMethodID !== method.paymentMethodID);
    await AsyncStorage.setItem(localPaymentMethodsKey(orgHandle), JSON.stringify([method, ...filtered]));
  } catch (e) {
    console.error('Error saving local payment method:', e);
  }
};

export const removeLocalPaymentMethod = async (orgHandle, paymentMethodID) => {
  try {
    const existing = await readLocalPaymentMethods(orgHandle);
    await AsyncStorage.setItem(localPaymentMethodsKey(orgHandle), JSON.stringify(existing.filter((m) => m.paymentMethodID !== paymentMethodID)));
  } catch (e) {
    console.error('Error removing local payment method:', e);
  }
};

export const RAINFOREST_TERMS_URL = 'https://www.reachchms.com/terms#payment-processing-agreement';

export const getOnboardingUrl = ({ sessionKey, merchantID, merchantApplicationID, termsUrl = RAINFOREST_TERMS_URL, theme = 'light', bg }) => {
  const q = new URLSearchParams({
    session_key: sessionKey,
    merchant_id: merchantID,
    merchant_application_id: merchantApplicationID,
    bundle_url: getBundleUrl(sessionKey, 'merchant'),
    theme, // 'light' | 'dark' — drives style props in onboarding.html (see §9.1)
  });
  if (termsUrl) q.set('terms_url', termsUrl);
  if (bg) q.set('bg', bg);
  // Relative path. The native WebView wrapper prefixes EXPO_PUBLIC_APP_WEB_URL (see FormEmbed.native.jsx).
  return `/rainforest/onboarding.html?${q.toString()}`;
};

export const getPaymentUrl = ({ sessionKey, payinConfigID, paymentMethodID, allowedMethods = 'CARD,ACH,APPLE_PAY', autoSubmit = false, apiVersion = '2024-10-16', theme = 'light', bg }) => {
  const q = new URLSearchParams({
    session_key: sessionKey,
    payin_config_id: payinConfigID,
    bundle_url: getBundleUrl(sessionKey, 'payment'),
    api_version: apiVersion,
    theme,
  });
  if (paymentMethodID) q.set('payment_method_id', paymentMethodID);
  if (autoSubmit) q.set('auto_submit', 'true');
  if (!paymentMethodID) q.set('allowed_methods', allowedMethods);
  if (bg) q.set('bg', bg);
  // Relative path. The native WebView wrapper prefixes EXPO_PUBLIC_APP_WEB_URL (see FormEmbed.native.jsx).
  return `/rainforest/payment.html?${q.toString()}`;
};

export const getPaymentMethodConfigUrl = ({ sessionKey, paymentMethodConfigID, apiVersion = '2024-10-16', theme = 'light', bg }) => {
  const q = new URLSearchParams({
    session_key: sessionKey,
    payment_method_config_id: paymentMethodConfigID,
    allowed_methods: 'CARD,ACH,APPLE_PAY',
    bundle_url: getBundleUrl(sessionKey, 'payment'),
    api_version: apiVersion,
    theme,
  });
  if (bg) q.set('bg', bg);
  return `/rainforest/payment.html?${q.toString()}`;
};

const isAmexBrand = (brand) => {
  const normalized = String(brand || '').replace(/[^a-z]/gi, '').toUpperCase();
  return normalized === 'AMEX' || normalized === 'AMERICANEXPRESS';
};

export const calculateGivingCharge = (giftCents, paymentMethod, feeSummary, coverFees = true) => {
  const gift = Number(giftCents);
  if (!Number.isInteger(gift) || gift < 0 || !isPaymentMethodUsable(paymentMethod) || !feeSummary) return null;
  if (!feeSummary.syncedAt || Number.isNaN(Date.parse(feeSummary.syncedAt))) return null;

  const methodType = String(paymentMethod.methodType || '').toUpperCase();
  const isAch = methodType === 'ACH' || methodType === 'BANK';
  // Apple Pay bills at card rates; cardBrand is populated so the AMEX
  // surcharge path below applies when the wallet card is Amex.
  const isCard = methodType === 'CARD' || methodType === 'APPLE_PAY';
  if (!isAch && !isCard) return null;
  if (!coverFees) return { giftAmount: gift, convenienceFee: 0, grossAmount: gift, methodType: isAch ? 'ACH' : methodType };

  const fixed = Number(isAch ? feeSummary.achFeeCents : feeSummary.onlineCardFeeCents);
  let rate = Number(isAch ? feeSummary.achRatePCM : feeSummary.onlineCardRatePCM);
  if (!Number.isFinite(fixed) || !Number.isFinite(rate)) return null;
  if (isCard && isAmexBrand(paymentMethod.cardBrand || paymentMethod.brand)) {
    const amexSurcharge = Number(feeSummary.cardAmexRateSurchargePCM);
    if (!Number.isFinite(amexSurcharge)) return null;
    rate += amexSurcharge;
  }

  const denominator = 100000;
  if (rate < 0 || rate >= denominator || fixed < 0) return null;
  let grossAmount = Math.ceil(((gift + fixed) * denominator) / (denominator - rate));

  let cap = null;
  if (isAch) {
    cap = Number(feeSummary.achRateCapCents);
    if (!Number.isFinite(cap) || cap < 0) return null;
    const uncappedRateFee = Math.round((grossAmount * rate) / denominator);
    if (uncappedRateFee > cap) grossAmount = gift + fixed + cap;
  }

  // Rainforest rounds the percentage portion of the fee to the nearest cent
  // before adding the fixed fee, so the continuous estimate above can be a
  // cent high (or, in edge cases, low). Walk the gross to the minimal value
  // that still covers the fee as the processor actually computes it.
  // Mirrors calculateGrossUp in supabase/functions/_shared/giving.ts.
  const processorFee = (g) => {
    const ratePart = Math.round((g * rate) / denominator);
    const capped = cap != null ? Math.min(ratePart, cap) : ratePart;
    return capped + fixed;
  };
  while (grossAmount - gift < processorFee(grossAmount)) grossAmount++;
  while (grossAmount - 1 - gift >= processorFee(grossAmount - 1)) grossAmount--;

  return {
    giftAmount: gift,
    convenienceFee: grossAmount - gift,
    grossAmount,
    methodType: isAch ? 'ACH' : methodType,
  };
};

// Opt-out notice under the Give button. Hidden until a payment method is
// chosen; ACH is offered as an opt-out only for card selections (special
// billing profiles can quote the flat $0.25 ACH fee, others stay generic).
export const getGivingFeeNotice = (paymentMethod, feeSummary, orgName, allowFeeChoice = false) => {
  if (!paymentMethod) return null;
  if (allowFeeChoice) return `Your total includes processing fees so ${orgName || 'the organization'} receives the full amount. Turn off Cover Fees to opt out.`;
  const base = `We have increased your donation so that ${orgName || 'the organization'} receives the full amount you intended.`;
  // Only ACH/BANK get the non-card wording — card methods (incl. APPLE_PAY)
  // are offered the cheaper ACH opt-out.
  const methodType = String(paymentMethod.methodType || '').toUpperCase();
  if (methodType === 'ACH' || methodType === 'BANK') {
    return `${base} To opt out of this adjustment, you may give in person.`;
  }
  return feeSummary?.isSpecial
    ? `${base} To opt out of this adjustment, you may give in person or via ACH for only $0.25.`
    : `${base} To opt out of this adjustment, you may give in person or via ACH for a lower rate.`;
};

export const getRecurringGifts = async ({ organizationID, personID }) => {
  const { data, error } = await supabase
    .from('GivingRecurringGifts')
    .select('*')
    .eq('organizationID', organizationID)
    .eq('personID', personID)
    .order('nextRunAt', { ascending: true });
  if (error) ErrorHandler(Error(error.message), { query: 'getRecurringGifts', organizationID, personID, error }, 'Failed to load recurring gifts');
  return data || [];
};

/**
 * Org-wide recurring gifts for admin reporting. No FKs exist on
 * GivingRecurringGifts, so we fetch gifts then batch-merge People and
 * GivingPaymentMethods in JS to populate giver name + payment method details.
 */
export const getOrgRecurringGifts = async ({ organizationID }) => {
  const { data: gifts, error } = await supabase
    .from('GivingRecurringGifts')
    .select('*')
    .eq('organizationID', organizationID)
    .order('nextRunAt', { ascending: true });
  if (error) {
    ErrorHandler(Error(error.message), { query: 'getOrgRecurringGifts', organizationID, error }, 'Failed to load recurring gifts');
    return [];
  }
  if (!gifts || gifts.length === 0) return [];

  const personIDs = [...new Set(gifts.map((g) => g.personID))];
  const paymentMethodIDs = [...new Set(gifts.map((g) => g.paymentMethodID))];

  const [peopleRes, methodsRes] = await Promise.all([
    supabase.from('People').select('id, firstName, lastName').in('id', personIDs),
    supabase
      .from('GivingPaymentMethods')
      .select('paymentMethodID, methodType, cardBrand, cardLast4, achAccountNumberLast4, achBankName')
      .in('paymentMethodID', paymentMethodIDs),
  ]);

  const peopleById = new Map((peopleRes.data || []).map((p) => [p.id, p]));
  const methodsById = new Map((methodsRes.data || []).map((m) => [m.paymentMethodID, m]));

  return gifts.map((gift) => ({
    ...gift,
    person: peopleById.get(gift.personID) || null,
    paymentMethod: methodsById.get(gift.paymentMethodID) || null,
  }));
};

export const getReportSession = async (organizationID, reportType) => {
  const { data, error } = await supabase.functions.invoke('rainforest-report-session', { body: { organizationID, reportType } });
  if (error) throw error;
  return data;
};

export const REPORT_TYPES = {
  deposit: 'deposit',
  payment: 'payment',
  chargeback: 'chargeback',
};

export const getReportUrl = ({ type, sessionKey, merchantID, timezone }) => {
  const q = new URLSearchParams({
    session_key: sessionKey,
    merchant_id: merchantID,
    bundle_url: getBundleUrl(sessionKey, 'merchant'),
  });
  if (timezone) q.set('timezone', timezone);
  const file = type === REPORT_TYPES.payment
    ? 'payment-report.html'
    : type === REPORT_TYPES.chargeback
      ? 'chargeback-report.html'
      : 'deposit-report.html';
  return `/rainforest/${file}?${q.toString()}`;
};

export const createRecurringGift = async (payload) => {
  const { data, error } = await supabase.functions.invoke('rainforest-create-recurring-gift', { body: payload });
  const extracted = await extractEdgeFunctionError(error, data);
  if (extracted) throw new Error(extracted.message);
  if (!data?.recurringGiftID) throw new Error('Recurring gift was not saved');
  return data;
};

export const updateRecurringGift = async (payload) => {
  const { data, error } = await supabase.functions.invoke('rainforest-update-recurring-gift', { body: payload });
  if (error) throw error;
  return data;
};
